import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lightbulb, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "hb_bespaarcheck_dismissed";

const BespaarcheckPopup = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Do not show on admin pages
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) { /* ignore */ }
    if (dismissed) return;

    const delay = 3500 + Math.random() * 1500; // 3.5–5s
    const timer = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(timer);
  }, [isAdmin]);

  const handleClose = () => {
    setOpen(false);
    try { window.sessionStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
  };

  const handleCTA = () => {
    try { window.sessionStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  if (isAdmin) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-24 right-4 sm:right-6 z-40 w-[320px] sm:w-[360px] max-w-[calc(100vw-2rem)]"
          data-testid="bespaarcheck-popup"
          role="dialog"
          aria-label="Gratis bespaarcheck"
        >
          {/* glow */}
          <div className="absolute -inset-2 rounded-3xl bg-brand/20 blur-2xl opacity-60 pointer-events-none" />

          <div className="relative bg-white rounded-3xl border border-brand/15 shadow-[0_24px_60px_rgba(15,23,42,0.18)] p-5 sm:p-6 overflow-hidden">
            {/* corner accent */}
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full gradient-pink opacity-15 pointer-events-none" />

            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white text-slate-400 hover:bg-slate-100 hover:text-navy-900 flex items-center justify-center transition-colors"
              data-testid="bespaarcheck-close"
              aria-label="Sluiten"
            >
              <X size={16} />
            </button>

            <div className="relative flex items-start gap-3 pr-8">
              <div className="w-11 h-11 rounded-2xl gradient-pink text-white flex items-center justify-center flex-shrink-0">
                <Lightbulb size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-black text-navy-900 text-lg leading-tight">Gratis Bespaarcheck</div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  Ontdek binnen 2 minuten hoeveel u kunt besparen met een thuisbatterij.
                </p>
              </div>
            </div>

            <Link
              to="/#calculator"
              onClick={handleCTA}
              className="btn-pink !text-sm !w-full !justify-center mt-5"
              data-testid="bespaarcheck-cta"
            >
              Start Gratis Bespaarcheck
              <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BespaarcheckPopup;
